import React, { useRef, useState } from 'react';
import TextArea from 'devextreme-react/text-area';
import { Validator, RequiredRule } from 'devextreme-react/validator';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { PasteEvent } from 'devextreme/ui/text_box';
import { InfoIconButton } from './InfoIconButton';
import '../../scss/controls/_multilinetextbox.scss';

export enum MultilineTextBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

export enum MultilineTextBoxLabelSize {
    None,
    Small,
    Large,
}

/** Where the label sits relative to the text area. */
export enum MultilineTextBoxLayoutStyle {
    /** Label above the text area (default; the original layout). */
    Vertical = 'vertical',
    /** Label to the left of the text area, in a fixed-width column (see labelWidth). */
    Horizontal = 'horizontal',
}

interface MultilineTextBoxProps {
    id?: string;
    value?: string;
    readOnly?: boolean;
    onChange: (value: string) => void;
    label?: string;
    helperText?: string;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    height?: string;
    maxLength?: number;
    placeholder?: string;
    onPaste?: (e: PasteEvent) => void;
    componentSpacing?: MultilineTextBoxComponentSpacing;
    labelSize?: MultilineTextBoxLabelSize;
    validateOnFocusChange?: boolean;
    showInfoIcon?: boolean;
    infoText?: React.ReactNode;
    /** Label above the text area (Vertical, default) or beside it (Horizontal). */
    layoutStyle?: MultilineTextBoxLayoutStyle;
    /** Width of the label column when layoutStyle is Horizontal. Defaults to 120px. Ignored for Vertical. */
    labelWidth?: string;
}

export const MultilineTextBox: React.FC<MultilineTextBoxProps> = ({
    id,
    value,
    readOnly = false,
    onChange,
    label,
    helperText,
    required = false,
    children,
    validator,
    width = 'auto',
    height = 'auto',
    maxLength,
    placeholder,
    onPaste,
    componentSpacing = MultilineTextBoxComponentSpacing.StandardSpacing,
    labelSize = MultilineTextBoxLabelSize.None,
    validateOnFocusChange = true,
    showInfoIcon = false,
    infoText,
    layoutStyle = MultilineTextBoxLayoutStyle.Vertical,
    labelWidth,
}) => {
    // Vertical keeps the exact class name and inline style the control has always rendered, so existing
    // usages are unaffected; Horizontal only adds a class (+ the label width as a CSS variable).
    const isHorizontal = layoutStyle === MultilineTextBoxLayoutStyle.Horizontal;
    const containerClassName = `textarea-container ${componentSpacing}${isHorizontal ? ' horizontal' : ''}`;
    const containerStyle: React.CSSProperties = isHorizontal
        ? ({ width: width, '--textarea-label-width': labelWidth ?? '120px' } as React.CSSProperties)
        : { width: width };

    const [errorMessage, setErrorMessage] = useState('');
    const [fieldLengthError, set_fieldLengthError] = useState(false);
    const validatorRef = useRef<any>(null);

    const handleValueChanged = (e: any) => {
        onChange(e.value);
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        setErrorMessage(validatedInfo?.brokenRule?.message ?? '');
    };

    // Only on leaving the box, so clicking into an empty required field doesn't flag it before anything is typed
    const handleFocusOut = () => {
        if (validatorRef.current) {
            const result = validatorRef.current.instance.validate();
            if (!result.isValid) {
                // Assuming the first broken rule's message is the error message:
                setErrorMessage(result.brokenRules[0]?.message || '');
            } else {
                setErrorMessage('');
            }
        }
    };

    const onKeyUp = (e: any) => {
        const value = e.event.currentTarget.value;

        if (maxLength && value.length >= maxLength) {
            set_fieldLengthError(true);
        } else {
            set_fieldLengthError(false);
        }
    };

    const getLabelClassName = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return ' large';
            default:
                return '';
        }
    };

    const getControlSpacingTop = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return '22px';
            default:
                return;
        }
    };

    const getControlSpacingBottom = () => {
        switch (labelSize) {
            case MultilineTextBoxLabelSize.Large:
                return '20px';
            default:
                return;
        }
    };

    return (
        <div
            className={containerClassName}
            style={containerStyle}
        >
            {label && (
                <div>
                    <label
                        className={`textarea-label${getLabelClassName()}${errorMessage ? ' error' : ''}`}
                        style={{ marginTop: getControlSpacingTop(), marginBottom: getControlSpacingBottom() }}
                    >
                        {required ? label + ' *' : label}
                    </label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='textarea-wrapper'
                style={{ position: 'relative' }}
            >
                <TextArea
                    id={id}
                    value={value}
                    valueChangeEvent='change'
                    readOnly={readOnly}
                    onValueChanged={handleValueChanged}
                    onFocusOut={validateOnFocusChange ? handleFocusOut : undefined}
                    onKeyUp={onKeyUp}
                    className={`textarea-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    height={height}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    onPaste={onPaste}
                >
                    <Validator
                        ref={validatorRef}
                        onValidated={onValidated}
                    >
                        {required && <RequiredRule message='This field is required' />}
                        {validator}
                    </Validator>
                </TextArea>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='textarea-error'>{errorMessage}</div>
            ) : maxLength && fieldLengthError ? (
                <div className='textarea-error'>This field will not allow more than {maxLength} characters. </div>
            ) : (
                helperText && <div className='textarea-helper'>{helperText}</div>
            )}
            {children && <div className='textarea-children'>{children}</div>}
        </div>
    );
};
